import { type FunctionComponent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import useResizeObserver from 'use-resize-observer'
import { useOnClickOutside } from 'usehooks-ts'
import { DoNotBleachIcon } from '@velasca/icons/do-not-bleach'
import { DoNotUseDryerIcon } from '@velasca/icons/do-not-use-dryer'
import { DoNotUseTetrachlorethyleneIcon } from '@velasca/icons/do-not-use-tetrachlorethylene'
import { DryHorizontallyIcon } from '@velasca/icons/dry-horizontally'
import { IronAtMaximumIcon } from '@velasca/icons/iron-at-maximum'
import { PlayIcon } from '@velasca/icons/play'
import { WashAt30DegreeIcon } from '@velasca/icons/wash-at30-degree'
import { WashHandIcon } from '@velasca/icons/wash-hand'
import type { VideoSource } from '@velasca/types'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { Collapsible } from '../Collapsible/Collapsible'
import { CustomVideoPlayer } from '../CustomVideoPlayer/CustomVideoPlayer'
import { ResponsiveImage } from '../ResponsiveImage/ResponsiveImage'
import { SansTitle } from '../SansTitle/SansTitle'
import { VideoModal } from '../VideoModal/VideoModal'
import { VimeoVideo } from '../VimeoVideo/VimeoVideo'
import {
  BorderedImage,
  BorderedImageWrapper,
  Card,
  ClickArea,
  Content,
  ImageBlock,
  ImageBlockWrapper,
  Img,
  InstructionImages,
  InstructionsCollapse,
  Player,
  SignatureImage,
  SignatureWrapper,
  StyledReadMore,
  WrapIcon,
  WrapperImages,
  WrapperImg,
  WrapperText,
} from './DetailsCard.styles'
import type { DetailsCardProps } from './DetailsCard.types'

const config = {
  ironing: {
    1: IronAtMaximumIcon,
    2: null,
  },
  washing: {
    1: DoNotUseTetrachlorethyleneIcon,
    2: WashHandIcon,
    3: WashAt30DegreeIcon,
  },
  bleaching: {
    F: DoNotBleachIcon,
    T: null,
  },
  dryer: {
    1: DoNotUseDryerIcon,
    2: null,
    3: DryHorizontallyIcon,
  },
}

const collapsibleSectionFormula = {
  /**
   * The collapsible section is shown only if the description container element is 30% taller than the image.
   */
  MIN_HEIGHT_RATIO: 1.3,
  /**
   * The collapsed element height is 80% of the image height. We use the image height as the minimum height of the collapsible section.
   */
  COLLAPSED_HEIGHT_MULTIPLIER: 0.8,
}

export const DetailsCard = ({
  id,
  className = '',
  title,
  description,
  image,
  image02,
  image03,
  signatureImg,
  video,
  instructions,
  footerImages,
  videoUrl,
  hasMultipleImages = false,
  iconsId = {},
  isCollapsible = false,
}: DetailsCardProps): JSX.Element => {
  const { ref: imageRef, height: imageInferredHeight } =
    useResizeObserver<HTMLDivElement>()

  const descriptionRef = useRef<HTMLDivElement>(null)
  const { height: descriptionHeight = 0 } = useResizeObserver({
    ref: descriptionRef,
  })
  const [isExpanded, setIsExpanded] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false)
  const { t } = useTranslation('product')
  const imageHeight = imageInferredHeight || 286

  const shouldCollapse =
    descriptionHeight / imageHeight > collapsibleSectionFormula.MIN_HEIGHT_RATIO

  const videoRef = useRef<HTMLVideoElement>(null)

  useOnClickOutside<HTMLVideoElement>(videoRef, () => {
    setIsVideoModalOpen(false)
  })

  const icons = Object.keys(iconsId).flatMap((key) => {
    const iconConfig = config[key as keyof typeof config]

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!iconConfig) {
      return []
    }

    const icon = iconConfig[
      iconsId[key] as keyof typeof iconConfig
    ] as FunctionComponent<{
      className: string
    }> | null

    return icon ? [icon] : []
  })

  return (
    <>
      {isVideoModalOpen && (video ?? videoUrl) && (
        <VideoModal setIsModalOpen={setIsVideoModalOpen}>
          {videoUrl ? (
            <VimeoVideo url={videoUrl.url} />
          ) : (
            <Player
              loop
              autoPlay
              controls
              controlsList="nodownload"
              ref={videoRef}
              onContextMenu={(e) => {
                // needed to prevent video download on right click.
                e.preventDefault()

                return false
              }}
            >
              {video?.sources.map(({ src, mimeType }: VideoSource) => (
                <source key={src} src={src} type={mimeType} />
              ))}
            </Player>
          )}
        </VideoModal>
      )}

      <Card id={id}>
        <Content hasImage={!!image}>
          <WrapperText className={className} cardHasImages={hasMultipleImages}>
            <SansTitle fontWeight="500" margin="0 0 0.5rem 0" as="h2">
              {title}
            </SansTitle>

            {/* Inline video shown only on mobile */}
            {image02?.src && (
              <CustomVideoPlayer
                isMobile
                image={image02}
                src={videoUrl?.url}
                onClick={() => {
                  setIsVideoModalOpen(true)
                }}
              />
            )}

            {instructions
              ? description && (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: description }} />
                    <InstructionsCollapse
                      isOpen={isExpanded}
                      title={
                        isExpanded
                          ? t('closedInstructions')
                          : t('expandedInstructions')
                      }
                      onClick={() => {
                        setIsExpanded((prevState) => !prevState)
                      }}
                    />
                    <Collapsible
                      isExpanded={isExpanded}
                      condition={Boolean(instructions)}
                      height={isExpanded ? 'auto' : 0}
                    >
                      <>
                        {instructions && (
                          <div
                            dangerouslySetInnerHTML={{ __html: instructions }}
                          />
                        )}
                      </>
                    </Collapsible>
                  </>
                )
              : description && (
                  <>
                    <Collapsible
                      hasFading
                      isExpanded={isExpanded || !shouldCollapse}
                      condition={isCollapsible}
                      height={
                        isExpanded || !shouldCollapse
                          ? 'auto'
                          : imageHeight *
                            collapsibleSectionFormula.COLLAPSED_HEIGHT_MULTIPLIER
                      }
                    >
                      <div
                        ref={descriptionRef}
                        dangerouslySetInnerHTML={{ __html: description }}
                      />

                      {!isEmpty(icons) && (
                        <WrapIcon>
                          {icons.map((IconComponent, index) => {
                            return (
                              <IconComponent
                                className={String(IconComponent)}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                              />
                            )
                          })}
                        </WrapIcon>
                      )}
                    </Collapsible>

                    {isCollapsible && shouldCollapse && (
                      <StyledReadMore
                        title={isExpanded ? t('readLess') : t('readMore')}
                        isOpen={isExpanded}
                        onClick={() => {
                          setIsExpanded((prevState) => !prevState)
                        }}
                      />
                    )}
                  </>
                )}

            {signatureImg?.src && (
              <SignatureWrapper>
                <SignatureImage
                  lqip
                  lazyload
                  alt={signatureImg.alt}
                  sources={buildIntrinsicWidthSources({
                    src: signatureImg.src,
                    maxWidth: signatureImg.width,
                  })}
                  imgSrc={setQueryParams(signatureImg.src, [
                    { name: 'width', value: '30' },
                  ])}
                />
              </SignatureWrapper>
            )}
          </WrapperText>

          {image && !hasMultipleImages && (
            <WrapperImg>
              {(videoUrl ?? video) && (
                <ClickArea
                  onClick={() => {
                    setIsVideoModalOpen(true)
                  }}
                >
                  <PlayIcon width="50" height="50" />
                </ClickArea>
              )}
              {Boolean(image.src) && (
                <Img
                  lqip
                  lazyload
                  ref={imageRef}
                  alt={image.alt}
                  sources={buildIntrinsicWidthSources({
                    src: image.src,
                    maxWidth: image.width,
                  })}
                  imgSrc={setQueryParams(image.src, [
                    { name: 'width', value: '30' },
                  ])}
                />
              )}
            </WrapperImg>
          )}
          {(image ?? image02 ?? image03) && hasMultipleImages ? (
            <WrapperImages>
              {image?.src && (
                <BorderedImageWrapper>
                  <BorderedImage
                    lqip
                    lazyload
                    alt={image.alt}
                    sources={buildIntrinsicWidthSources({
                      src: image.src,
                      maxWidth: image.width,
                    })}
                    imgSrc={setQueryParams(image.src, [
                      { name: 'width', value: '30' },
                    ])}
                  />
                </BorderedImageWrapper>
              )}
              {image03?.src && (
                <ImageBlockWrapper>
                  <ImageBlock
                    lqip
                    lazyload
                    alt={image03.alt}
                    sources={buildIntrinsicWidthSources({
                      src: image03.src,
                      maxWidth: image03.width,
                    })}
                    imgSrc={setQueryParams(image03.src, [
                      { name: 'width', value: '30' },
                    ])}
                  />
                </ImageBlockWrapper>
              )}
              {/* Inline video */}
              {image02?.src && (
                <CustomVideoPlayer
                  image={image02}
                  src={videoUrl?.url}
                  onClick={() => {
                    setIsVideoModalOpen(true)
                  }}
                />
              )}
            </WrapperImages>
          ) : null}
        </Content>
        {footerImages && !isEmpty(footerImages) && isExpanded && (
          <InstructionImages>
            {footerImages.map(({ src, alt }) => {
              return (
                <ResponsiveImage
                  lqip
                  lazyload
                  key={src}
                  alt={alt}
                  sources={buildIntrinsicWidthSources({ src })}
                  imgSrc={setQueryParams(src, [{ name: 'width', value: '30' }])}
                />
              )
            })}
          </InstructionImages>
        )}
      </Card>
    </>
  )
}
